import { Component } from "@angular/core";
import {
  ViewController,
  NavParams,
  ToastController,
  LoadingController
} from "ionic-angular";
import { AppState } from "../../app/models/app.state";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { _i18n_str } from "../../assets/i18n/non-static-ids";
import { FormBuilder, Validators, FormGroup } from "@angular/forms";
import { DashboardService } from "../../app/services/dashboard.service";
import { WidgetConfig } from "@zitelab/ionic-fhir-questionnaires/dist/models/widget";
import { OptionObject } from "@zitelab/ionic-fhir-questionnaires";
import { IManualItem } from "../../app/models/manual-item";
import { ManualItemsService } from "../../app/services/manual-items.service";
import { diaprofilTopics, manualItemTypeOptions, scores, careTopics } from "../../app/models/types";
import { manualItemTypeValidator } from "../../app/validators/manual-item-type-validator.directive";
import { relevantTopicsValidator } from "../../app/validators/relevant-topics-validator.directive";

// https://github.com/ionicthemes/ionic-forms-and-validations/tree/master/ionic-3

@Component({
  selector: "manage-manual-item-modal",
  templateUrl: "manage-manual-item.modal.html"
})
export class ManageManualItemModal {
  modalType: "EDIT" | "CREATE";
  selectableWidgets: WidgetConfig<any>[] = [];
  manualItem: IManualItem;
  form: FormGroup;
  consultationTopicOptions: OptionObject[];
  diaprofilTopics = diaprofilTopics;
  manualItemTypeOptions = manualItemTypeOptions;
  scores = scores;
  careTopics = careTopics;
  constructor(
    public viewCtrl: ViewController,
    private navParams: NavParams,
    public manual: ManualItemsService,
    public dashboard: DashboardService,
    public toastCtrl: ToastController,
    private fb: FormBuilder,
    public store: Store<AppState>,
    public loadingCtrl: LoadingController,
    public translate: TranslateService
  ) {
    this.consultationTopicOptions = this.dashboard.getConsultationTopicOptions();
    this.selectableWidgets = this.dashboard.widgets.filter(
      widget => widget.widgetType !== "LabelWidget" && widget.prefix !== null
    );
    this.modalType = this.navParams.get("modalType") || "CREATE";
    this.manualItem =
      (this.navParams.get("manualItem") as IManualItem) || <IManualItem>{};
    this.form = this.fb.group(
      {
        itemType: [
          this.manualItem.itemType,
          Validators.compose([Validators.required])
        ],
        relevantTopics: [this.manualItem.relevantTopics],
        defaultTopics: [this.manualItem.defaultTopics],
        questionnaireItemId: [this.manualItem.questionnaireItemId],
        consultationTopicId: [this.manualItem.consultationTopicId],
        patientWishId: [this.manualItem.patientWishId],
        scoreId: [this.manualItem.scoreId],
        dashboardAreaId: [this.manualItem.dashboardAreaId],
        manualText: [
          this.manualItem.manualText,
          Validators.compose([Validators.required])
        ]
      },
      {
        validator: Validators.compose([
          manualItemTypeValidator(),
          relevantTopicsValidator()
        ])
      }
    );
  }

  dismiss($event) {
    this.viewCtrl.dismiss();
  }

  submitManualItem($event) {
    if (this.modalType === "CREATE") {
      this.createManualItem($event);
    } else {
      this.patchManualItem($event);
    }
  }

  selectProItemFilter(term: string, item: WidgetConfig<any>) {
    return `${item.prefix} ${item.label}`.indexOf(term) > -1;
  }

  cleanManualItem(manualItem: IManualItem): IManualItem {
    switch (manualItem.itemType) {
      case "CONSULTATION_TOPIC": {
        manualItem.patientWishId = null;
        manualItem.questionnaireItemId = null;
        manualItem.scoreId = null;
        manualItem.dashboardAreaId = null;
        manualItem.title = manualItem.consultationTopicId;
        break;
      }
      case "DIAPROFIL_PRO_ITEM": {
        manualItem.patientWishId = null;
        manualItem.consultationTopicId = null;
        manualItem.scoreId = null;
        manualItem.dashboardAreaId = null;
        manualItem.title = manualItem.questionnaireItemId;
        break;
      }
      case "SCORE": {
        manualItem.patientWishId = null;
        manualItem.consultationTopicId = null;
        manualItem.questionnaireItemId = null;
        manualItem.dashboardAreaId = null;
        manualItem.title = manualItem.scoreId;
        break;
      }
      case "DASHBOARD_AREA": {
        manualItem.patientWishId = null;
        manualItem.consultationTopicId = null;
        manualItem.scoreId = null;
        manualItem.questionnaireItemId = null;
        manualItem.relevantTopics = [];
        manualItem.defaultTopics = [];
        manualItem.title = manualItem.dashboardAreaId;
        break;
      }
      default: {
        break;
      }
    }
    return manualItem;
  }

  createManualItem($event) {
    const manualItem = this.cleanManualItem(this.form.value as IManualItem);
    const loading = this.loadingCtrl.create({
      content: this.translate.instant(
        _i18n_str("GENERIC_UI.CREATING_NEW_MANUAL_ITEM")
      ),
      dismissOnPageChange: true
    });
    loading.present();
    const extraAction = result => {
      loading.dismissAll();
      this.viewCtrl.dismiss();
      const toast = this.toastCtrl.create({
        message: `Manual element oprettet!`,
        duration: 3000,
        position: "top",
        closeButtonText: "Luk",
        showCloseButton: true
      });
      toast.present();
    };
    this.manual.postManualItem(manualItem, extraAction);
  }

  patchManualItem($event) {
    let manualItem = {
      ...{
        id: this.manualItem.id,
        sourceUrl: this.manualItem.sourceUrl,
        title: this.manualItem.title,
        itemType: this.manualItem.itemType,
        questionnaireItemId: this.manualItem.questionnaireItemId,
        consultationTopicId: this.manualItem.consultationTopicId,
        patientWishId: this.manualItem.patientWishId,
        scoreId: this.manualItem.scoreId,
        dashboardAreaId: this.manualItem.dashboardAreaId,
        manualText: this.manualItem.manualText,
        relevantTopics: this.manualItem.relevantTopics,
        defaultTopics: this.manualItem.defaultTopics,
        created: this.manualItem.created,
        modified: this.manualItem.modified
      },
      ...(this.form.value as IManualItem)
    };
    manualItem = this.cleanManualItem(manualItem);
    const loading = this.loadingCtrl.create({
      content: this.translate.instant(
        _i18n_str("GENERIC_UI.MODIFYING_MANUAL_ITEM")
      ),
      dismissOnPageChange: true
    });
    loading.present();
    const extraAction = result => {
      loading.dismissAll();
      this.viewCtrl.dismiss();
      const toast = this.toastCtrl.create({
        message: `Ændringer til manual element gemt!`,
        duration: 3000,
        position: "top",
        closeButtonText: "Luk",
        showCloseButton: true
      });
      toast.present();
    };
    this.manual.patchManualItem(manualItem, extraAction);
  }
}



// WEBPACK FOOTER //
// ./src/modals/manage-manual-item.modal/manage-manual-item.modal.ts